import { useParams } from 'react-router-dom';
import {
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetProjectByIdQuery,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React from 'react';
import { EditFreshdeskEmailAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailAutoSetupSettings/EditFreshdeskEmailAutoSetupSettings';
import { EditFreshdeskEmailAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailAdvancedSettings/EditFreshdeskEmailAdvancedSettings';
import { EditFreshdeskEmailApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailApiDetailsSettings/EditFreshdeskEmailApiDetailsSettings';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

interface FreshdeskEmailSettingsFormValues extends AutocloseFormValues {
  subdomain: string;
  name: string;
  include_subject_first_response: boolean;
  reply_to_note: boolean;
  api_key: string;
  include_custom_fields: boolean;
  custom_fields: { key?: string; id: string; name: string }[] | null;
  // Set by the auto-setup section's agent and group selects (numeric ids).
  responder_id?: number | string;
  responder_email?: string;
  escalate_assign_group_id?: number | string;
}

export const EditFreshdeskEmailIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const isSetupDone = integration?.integration_data?.is_setup;
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);
  const [setFreshdeskAutoSetup, { isLoading: isLoadingSetZendeskChatAutoSetup }] =
    useFreshdeskEmailIntegrationAutoSetupMutation();

  useLoaderWhile(isLoadingUpdateProject, isLoadingSetZendeskChatAutoSetup);

  const methods = useForm<FreshdeskEmailSettingsFormValues>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain,
      name: integration?.integration_data?.name,
      include_subject_first_response: integration?.integration_data?.include_subject_first_response,
      reply_to_note: integration?.integration_data?.reply_to_note,
      api_key: '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      include_custom_fields: integration?.integration_data?.include_custom_fields || false,
      custom_fields: integration?.integration_data?.custom_fields || null,
    },
    resolver: formResolver(
      object({
        subdomain: stringScheme({ required: false }).label('Zendesk subdomain'),
        api_key: stringScheme({ required: false }).label('API token'),
        email: stringScheme({ required: false }).label('Admin email address'),
      }),
    ),
  });

  const submit = async (data: FreshdeskEmailSettingsFormValues) => {
    if (data.responder_id) {
      await setFreshdeskAutoSetup({
        projectId: id,
        integrationId: integration.id,
        body: {
          responder_id: +data.responder_id,
          responder_email: data.responder_email,
          escalate_assign_group_id: data.escalate_assign_group_id
            ? +data.escalate_assign_group_id
            : 0,
        },
      });
    }

    delete data.responder_id;
    delete data.responder_email;
    delete data.escalate_assign_group_id;

    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (!data.api_key) {
      delete data.api_key;
    }

    if (data.include_custom_fields) {
      data.custom_fields = data.custom_fields.map((field) => {
        return { id: field.id, name: `${field.name}` };
      });
    } else {
      data.custom_fields = null;
    }

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
        subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
        name: currentProject.name,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          {!isSetupDone && (
            <AccordionItem
              value="auto-setup-settings"
              className={clsx('group bg-white border-b-[#A498FF4D]')}
            >
              <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
                <div className="flex row items-center justify-between w-full">
                  <div>
                    <p className={'text-[#7A6BE5] font-semibold text-sm'}>Auto Setup Settings</p>
                  </div>
                  <div>
                    <EditSettingsAccordionIcon
                      open={accordionValue.includes('ticket-status-settings')}
                    />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <EditFreshdeskEmailAutoSetupSettings integration={integration} />
              </AccordionContent>
            </AccordionItem>
          )}
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshdeskEmailAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshdeskEmailApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
